"use client";

import cn from "classnames";
import { FC, useEffect, useState } from "react";
import { getDataLinkAttributes } from "@/helpers";
import { onSearchSubmit } from "../Navigation.helpers";
import { GlobalNavigationProps } from "../GlobalNavigation";
import Link from "@/components/Link";
import { Arrow, ArrowNav, CaretLeftLine } from "@/icons";
import SearchBar from "./SearchBar";
import MobileNavigationMenuTabItem from "./MobileNavigationMenuTabItem";
import MobileNavigationCategoryTabItem from "./MobileNavigationCategoryTabItem";

export type MobileNavigationPanelProps = {
  isMenuOpen?: boolean;
  closeMenu?: () => void;
} & Pick<GlobalNavigationProps, "superHeader" | "menu">;

const MobileNavigationPanel: FC<MobileNavigationPanelProps> = ({
  isMenuOpen,
  superHeader,
  menu,
  closeMenu,
}) => {
  const [activeTab, setActiveTab] = useState<number | null>(null);
  const [subTab, setSubTab] = useState<number | null>(null);
  const [isDropdownClicked, setIsDropdownClicked] = useState(false);
  const [view, setView] = useState<"eyeBrowLinks" | null>(null);

  useEffect(() => {
    if (!isMenuOpen) {
      setActiveTab(null);
      setSubTab(null);
      setView(null);
    }
  }, [isMenuOpen]);

  const handleBackToMain = () => {
    setActiveTab(null);
    setSubTab(null);
    setView(null);
  };

  const handleBackToCategories = () => {
    setSubTab(null);
  };

  const currentData = activeTab !== null ? menu.items?.[activeTab] : null;
  const currentCategoryLinks =
    currentData?.categoryLinks && subTab !== null
      ? currentData.categoryLinks[subTab]
      : null;

  return (
    <div className="w-full bg-white">
      {activeTab === null ? (
        !!menu?.items && (
          <>
            <div className="border-swdc-silver-200 border-l-8 border-solid">
              <div className="relative top-[-0.063rem] z-10 py-2.5 pr-6 pl-4">
                <SearchBar
                  onSubmit={onSearchSubmit}
                  search={superHeader?.search}
                />
              </div>
              <ul>
                {menu?.items?.map((item, index) =>
                  item.href && item.title ? (
                    <li key={`${item.title}-${index}`}>
                      <Link
                        className="text-subhead-2xs text-swdc-black-600 active:text-swdc-blue-500 flex w-full flex-row items-center justify-between py-4 pr-6 pl-4 text-left font-bold hover:underline active:underline"
                        {...getDataLinkAttributes({
                          dld: item.dld,
                          dlt: item.dlt,
                        })}
                        href={item.href ?? ""}
                      >
                        {item.title}
                      </Link>
                    </li>
                  ) : (
                    <li key={`${item.title}-${index}`}>
                      <button
                        onClick={() => {
                          setActiveTab(index);
                          setIsDropdownClicked(false);
                        }}
                        {...getDataLinkAttributes({
                          dld: item.dld,
                          dlt: item.dlt,
                        })}
                        className="text-subhead-2xs text-swdc-black-600 active:text-swdc-blue-500 flex w-full flex-row items-center justify-between py-4 pr-6 pl-4 text-left font-bold hover:underline active:underline"
                      >
                        <span className="flex flex-row gap-x-2">
                          {item.title}
                        </span>
                        <Arrow className="fill-swdc-gray-400 ml-2 h-3 w-[1.488rem]" />
                      </button>
                    </li>
                  ),
                )}
              </ul>
            </div>
            <div
              className={cn(
                superHeader?.items?.length
                  ? "border-swdc-silver-200 border-t border-solid py-4 pr-6 pl-4"
                  : null,
              )}
            >
              <ul>
                {superHeader?.items?.map((link, index) => {
                  if (!!link.href) {
                    const title = link.title;
                    if (!title) return null;

                    return (
                      <li key={`${link.title}-${index}`}>
                        <Link
                          href={link.href ?? ""}
                          {...getDataLinkAttributes({
                            dld: link.dld,
                            dlt: link.dlt,
                          })}
                          className="text-link-s text-swdc-black-600 active:text-swdc-blue-500 block py-4.5 pl-2 hover:underline active:underline"
                          onClick={closeMenu}
                        >
                          <span>{title}</span>
                        </Link>
                      </li>
                    );
                  }

                  if (!!link.links?.length) {
                    return (
                      <li key={`${link.title}-${index}`}>
                        <button
                          onClick={() => {
                            setView("eyeBrowLinks");
                            setActiveTab(index);
                            setIsDropdownClicked(true);
                          }}
                          className="text-link-s text-swdc-black-600 active:text-swdc-blue-500 flex w-full flex-row items-center justify-between py-4.5 pl-2 text-left hover:underline active:underline"
                        >
                          <span>{link.title}</span>
                          <ArrowNav />
                        </button>
                      </li>
                    );
                  }
                })}
              </ul>
            </div>
          </>
        )
      ) : (
        <div className={cn(isMenuOpen ? "inset-0 z-20 bg-white" : "hidden")}>
          <>
            {subTab === null && (
              <button
                {...getDataLinkAttributes({
                  dld:
                    menu?.mobileBackLink?.dld ??
                    `${menu?.mobileBackLink?.backToText} ${menu?.mobileBackLink?.mainMenuText}`,
                  dlt: menu?.mobileBackLink?.dlt ?? "Top Menu",
                })}
                onClick={handleBackToMain}
                className="border-b-swdc-silver-200 border-l-swdc-silver-200 text-link-s text-swdc-blue-500 active:text-swdc-blue-600 flex w-full flex-row items-center gap-x-1 border-b border-l-8 border-solid pt-4.5 pr-6 pb-4.25 pl-4 hover:underline active:underline"
              >
                <CaretLeftLine
                  className="mr-px -ml-0.75 size-4.5"
                  fill="#006482"
                />

                {`${menu?.mobileBackLink?.backToText} ${menu?.mobileBackLink?.mainMenuText}`}
              </button>
            )}

            {(() => {
              switch (view) {
                case "eyeBrowLinks":
                  return (
                    <MobileNavigationMenuTabItem
                      title={superHeader.items?.[activeTab ?? 0]?.title}
                      links={superHeader.items?.[activeTab ?? 0]?.links?.map(
                        (linkItem) => ({
                          ...linkItem,
                          ...getDataLinkAttributes({
                            dld: linkItem.dld,
                            dlt: linkItem.dlt,
                          }),
                        }),
                      )}
                      setSubTab={setSubTab}
                      closeMenu={closeMenu}
                    />
                  );
                default:
                  return subTab === null ? (
                    <MobileNavigationCategoryTabItem
                      title={currentData?.title}
                      links={currentData?.categoryLinks?.map((link) => ({
                        title: link.categoryTitle ?? "",
                        dld: link.categoryTitleMobileDLD ?? "",
                        dlt: link.categoryTitleMobileDLT ?? "",
                      }))}
                      setSubTab={setSubTab}
                      closeMenu={closeMenu}
                    />
                  ) : (
                    <>
                      {
                        <button
                          {...getDataLinkAttributes({
                            dld:
                              menu?.mobileBackLink?.dld ??
                              `${menu?.mobileBackLink?.backToText} ${currentData?.title}`,
                            dlt: menu?.mobileBackLink?.dlt ?? "Top Menu",
                          })}
                          onClick={handleBackToCategories}
                          className="border-b-swdc-silver-200 border-l-swdc-silver-200 text-link-s text-swdc-blue-500 active:text-swdc-blue-600 flex w-full flex-row items-center gap-x-1 border-b border-l-8 border-solid pt-4.5 pr-6 pb-4.25 pl-4 hover:underline active:underline"
                        >
                          <CaretLeftLine
                            className="mr-px -ml-0.75 size-4.5"
                            fill="#006482"
                          />

                          {`${menu?.mobileBackLink?.backToText} ${currentData?.title}`}
                        </button>
                      }
                      <MobileNavigationMenuTabItem
                        title={currentCategoryLinks?.categoryTitle}
                        links={currentCategoryLinks?.links}
                        cta={currentCategoryLinks?.cta}
                        closeMenu={closeMenu}
                      />
                    </>
                  );
              }
            })()}
          </>
          <div className="border-swdc-silver-300 border-b border-solid">
            {!isDropdownClicked &&
              (() => {
                const activePanel = menu.items?.[activeTab];
                if (!activePanel?.leftCta) {
                  return null;
                }
                return (
                  activePanel.title && (
                    <div className="border-swdc-silver-300 border-l-8 border-solid py-4 pr-6 pl-4">
                      {activePanel.leftCta}
                    </div>
                  )
                );
              })()}
          </div>
          <ul className="pt-4">
            {menu.items?.[0]?.leftLinks?.map((link, index) => (
              <li key={`${link.title}-${index}`}>
                <Link
                  href={link.href ?? ""}
                  {...getDataLinkAttributes({
                    dlt: link.dlt,
                    dld: link.dld,
                  })}
                  onClick={closeMenu}
                  className="text-link-s text-swdc-black-600 active:text-swdc-blue-500 flex w-full flex-row items-center justify-between py-4.5 pl-6 text-left hover:underline active:underline"
                >
                  {link.title}
                </Link>
              </li>
            ))}
          </ul>
          <ul>
            {menu.items?.[0]?.bottomLinks?.map((links, index) => (
              <li key={`${links.title}-${index}`}>
                <Link
                  href={links.href ?? ""}
                  {...getDataLinkAttributes({
                    dld: links.dld,
                    dlt: links.dlt,
                  })}
                  onClick={closeMenu}
                  className="text-link-s text-swdc-black-600 active:text-swdc-blue-500 flex w-full flex-row items-center justify-between py-4.5 pl-6 text-left hover:underline active:underline"
                >
                  {links.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MobileNavigationPanel;
